import FullViewLoading from 'components/common/FullViewLoading';
import IsAxiosError from 'components/common/IsAxiosError';
import type { EScoringBuildSystem } from 'pages/score/ScoreInterviewPage';
import type { ReactNode } from 'react';
import { createContext, useContext, useEffect, useState } from 'react';
import {
  useGetScoreInterviewByIdQuery,
  useManuallyScoreInterviewByIdMutation,
  useReScoreInterviewByIdMutation,
} from 'rtk/query/apis/scores';
import type { ExternalToast } from 'sonner';
import { toast } from 'sonner';
import type { TApplication } from 'types/application.types';
import type { TCandidate } from 'types/candidate.types';
import type { TSelectBase } from 'types/general.types';
import type { TInterviewClass, TInterviewRelations } from 'types/interview.types';
import type { TInterviewSummary } from 'types/interviewSummary.types';
import type { TJob } from 'types/job.types';
import { isForm, isOpen } from '@hubertai/screening-question-types';
import type {
  TAutoUpdate,
  TChatCollection,
  TChatFilter,
  TInterviewScoreTable,
  TInterviewScoreTableDetail,
} from 'types/scores.types';
import { ScoreLimits } from 'types/scores.types';
import type { TScoringTemplate } from 'rtk/query/apis/scores';
import type { TAdminUser } from 'types/user.types';

function rescaleZeroToHundred(value: number, oldMin: number, oldMax: number) {
  const oldRange = oldMax - oldMin;
  const newRange = ScoreLimits.Max - ScoreLimits.Min;
  return ((value - oldMin) * newRange) / oldRange;
}

export enum EPreferredScoringLanguage {
  Original = 'original',
  English = 'en',
}

export type ScoreInterviewContextType = {
  user: TAdminUser;
  job?: TJob;
  template?: TScoringTemplate;
  application?: TApplication;
  interview?: TInterviewClass & { autoUpdate?: TAutoUpdate; isNewBuild?: boolean };
  candidate?: TCandidate;

  // Interview summary - existing and new
  summary?: TInterviewSummary;
  existingInterviewSummary?: TInterviewSummary;

  aiDetectionSummary: TInterviewRelations['aiDetectionSummary'] | undefined;
  autoUpdateSummary: TInterviewRelations['autoUpdateSummary'] | undefined;
  chat?: TChatCollection[];
  scoreTable?: TInterviewScoreTable;
  setScoreTable: (x: TInterviewScoreTable) => void;

  scoreTableDetails: TInterviewScoreTableDetail[];

  languageList: TSelectBase[];
  setLanguageList: (data: any) => void;

  preferredLanguage?: EPreferredScoringLanguage;
  setPreferredLanguage: (data: any) => void;

  updateIntegration: boolean;
  setUpdateIntegration: (x: boolean) => void;

  chatFilter: TChatFilter;
  setChatFilter: (x: TChatFilter) => void;
  resetChatFilter: (onFilterDone?: () => void) => void;

  updateScoreData: (questionId: string, value: number, formId?: string, categoryId?: string) => void;
  saveScores: () => void;
  isManuallyScoreInterviewByIdRequestLoading: boolean;
  reScore: () => void;
  isReScoreInterviewByIdRequestLoading: boolean;
};

const ScoreInterviewContext = createContext<ScoreInterviewContextType | null>(null);

interface ScoreInterviewProviderProps {
  interviewId: string;
  user: TAdminUser;
  scoringBuildSystem?: EScoringBuildSystem;
  children: ReactNode;
}

export const ScoreInterviewProvider = ({
  interviewId,
  user,
  scoringBuildSystem,
  children,
}: ScoreInterviewProviderProps) => {
  const scoreInterviewQuery = useGetScoreInterviewByIdQuery(
    { interviewId, scoringBuildSystem },
    { skip: !interviewId },
  );

  const [template, setTemplate] = useState<TScoringTemplate>();
  const [job, setJob] = useState<TJob>();
  const [candidate, setCandidate] = useState<TCandidate>();
  const [application, setApplication] = useState<TApplication>();
  const [interview, setInterview] = useState<TInterviewClass>();

  //* Interview Summaries - as seen in recruiter app *//
  // Preview summary
  const [summary, setSummary] = useState<TInterviewSummary>();
  // Existing summary (if such is)
  const [existingInterviewSummary, setExistingInterviewSummary] = useState<TInterviewSummary | undefined>();

  const [aiDetectionSummary, setAiDetectionSummary] = useState<TInterviewRelations['aiDetectionSummary'] | undefined>();
  const [autoUpdateSummary, setAutoUpdateSummary] = useState<TInterviewRelations['autoUpdateSummary'] | undefined>();
  const [chat, setChat] = useState<TChatCollection[] | undefined>([]);
  const [updateIntegration, setUpdateIntegration] = useState<boolean>(true);
  const [chatFilter, setChatFilter] = useState<TChatFilter>({
    isFiltered: false,
    filterId: '',
    questionIds: [],
  });

  const [scoreTable, setScoreTable] = useState<TInterviewScoreTable>();
  const [scoreTableDetails, setScoreTableDetails] = useState<TInterviewScoreTableDetail[]>([]);

  const [preferredLanguage, setPreferredLanguage] = useState<EPreferredScoringLanguage>();
  const [languageList, setLanguageList] = useState<TSelectBase[]>([
    { label: 'Original', value: EPreferredScoringLanguage.Original },
    { label: 'English', value: EPreferredScoringLanguage.English },
  ]);

  const [manuallyScoreInterviewByIdRequest, { isLoading: isManuallyScoreInterviewByIdRequestLoading }] =
    useManuallyScoreInterviewByIdMutation();

  const [reScoreInterviewByIdRequest, { isLoading: isReScoreInterviewByIdRequestLoading }] =
    useReScoreInterviewByIdMutation();

  useEffect(() => {
    console.log('scoreInterviewQuery.data', scoreInterviewQuery.data);

    if (scoreInterviewQuery.data) {
      const data = scoreInterviewQuery.data;
      const interview = data.interview;
      const template = data.template;
      const relations = interview.relations;

      setTemplate(template);

      if (!interview) {
        toast.error('No interview available');
        return;
      }

      if (interview.scoreTable && interview.scoreTable.details) {
        const scoreTable = { ...interview.scoreTable };

        let details: TInterviewScoreTableDetail[] = [];
        if ('details' in scoreTable) {
          details = scoreTable.details ?? [];
          scoreTable.details = [];
        }
        setScoreTable(scoreTable);
        setScoreTableDetails(details);
      }

      if (interview.summary) {
        setSummary(interview.summary);
      }

      if (relations) {
        setJob(relations.job);
        setCandidate(relations.candidate);
        setApplication(relations.application);
        setInterview(interview);
        setChat(relations.chat);
        setAiDetectionSummary(relations.aiDetectionSummary);
        setAutoUpdateSummary(relations.autoUpdateSummary);
        setExistingInterviewSummary(relations.existingInterviewSummary);
      }
      // setData({ ...scoreInterviewQuery.data })
    }
  }, [scoreInterviewQuery.data]);

  useEffect(() => {
    if (interview && user && user.scoring_language_preference) {
      const preferences = user.scoring_language_preference;
      const mainLanguage = interview._state?.language;
      if (!mainLanguage) {
        setPreferredLanguage(EPreferredScoringLanguage.Original);
      }

      if (mainLanguage in preferences) {
        const preference = preferences[mainLanguage];
        if (preference === 'en') {
          setPreferredLanguage(EPreferredScoringLanguage.English);
        } else {
          setPreferredLanguage(EPreferredScoringLanguage.Original);
        }
      }
    }
  }, [interview, user]);

  useEffect(() => {
    console.log('preferredLanguage', preferredLanguage);
  }, [preferredLanguage]);

  const resetChatFilter = (onFilterDone?: () => void) => {
    setChatFilter({
      isFiltered: false,
      filterId: '',
      questionIds: [],
    });
    if (onFilterDone) {
      onFilterDone();
    }
  };

  const updateScoreData = (questionId: string, value: number, formId?: string, categoryId?: string) => {
    setScoreTableDetails(
      scoreTableDetails.map((detail) => {
        if (!chat || !chat.length) {
          toast.error('No chat.');
          return detail;
        }

        if (!questionId || !questionId.length) {
          toast.error('Missing question id.');
          return detail;
        }

        if (questionId !== detail.questionId) {
          return detail;
        }

        const collection = chat.find((x) => x.questionId === questionId);
        if (!collection) {
          toast.error('Missing collection.');
          return detail;
        }

        const question = collection.question;

        if (!question) {
          toast.error('Missing question.');
          return detail;
        }

        if (formId) {
          if (detail.formId && formId === detail.formId && isForm(question)) {
            const form = question.forms.find((form) => form.id === formId);

            if (!form) {
              toast.error('Missing form.');
              return detail;
            }

            if (detail.type === 'weighted') {
              const weightedMax = scoreTable!.weighted_max;
              const formWeight = detail.weight;

              if (typeof formWeight !== 'number') {
                toast.error('Wrong form weight type - not a number.');
                return detail;
              }

              const score = weightedMax * formWeight * (value / detail.scale[1]);
              const oldMax = weightedMax * formWeight;
              const displayedScore = rescaleZeroToHundred(score, 0, oldMax);

              console.log(score, displayedScore);
              return {
                ...detail,
                value,
                score,
                displayedScore,
                edited: true,
              };
            } else if (detail.type === 'bonus') {
              return {
                ...detail,
                value,
                score: value,
                displayedScore: value,
                computedValue: value,
                edited: true,
              };
            }

            toast.warning('Not available to edit.');
            return detail;
          }

          return detail;
        } else if (categoryId) {
          if (categoryId && isOpen(question)) {
            const weightedMax = scoreTable!.weighted_max;
            const formWeight = detail.weight;

            if (typeof formWeight !== 'number') {
              toast.error('Wrong form weight type - not a number.');
              return detail;
            }

            const score = weightedMax * formWeight * (value / detail.scale[1]);
            const oldMax = weightedMax * formWeight;
            const displayedScore = rescaleZeroToHundred(score, 0, oldMax);

            console.log(score, displayedScore);
            return {
              ...detail,
              value,
              score,
              displayedScore,
              edited: true,
            };
          }
          return detail;
        } else {
          if (detail.type === 'bonus') {
            console.log('detail.type === bonus', detail);
            const k = {
              ...detail,
              value,
              score: value,
              displayedScore: value,
            };
            console.log('detail.type === bonus k: ', k);
            return k;
          } else {
            toast.error('detail.type !== bonus');
            console.error('detail.type !== bonus', detail);
            return detail;
          }
        }
      }),
    );
  };

  const saveScores = async () => {
    if (!scoreTable) {
      toast.error('Missing score table.');
      return;
    }

    if (!chat) {
      toast.error('No chat.');
      return;
    }

    await manuallyScoreInterviewByIdRequest({
      interviewId,
      scoreTable,
      scoreTableDetails,
      chat,
      updateIntegration,
    })
      .unwrap()
      .then((x) => {
        const toastOptions: ExternalToast = {
          position: 'bottom-right',
          duration: Infinity,
        };

        if (x.score_update_result.status === 200) {
          toast.success(x.score_update_result.data.message, toastOptions);
        } else {
          toast.error(x.score_update_result.data.message, toastOptions);
        }

        if (x.integration_update_result.status === 200) {
          const msg = `${x.integration_update_result.data.message}.`;
          const platform =
            x.integration_update_result.data.platform && x.integration_update_result.data.platform.length
              ? x.integration_update_result.data.platform
              : null;
          toast.success(
            <>
              <b>Integration Updated.</b>
              <br />
              {msg}
              <br />
              {platform && ` Platform: ${platform}.`}
            </>,
            toastOptions,
          );
        } else {
          toast.error(`Integration update failed: ${x.integration_update_result.data.message}`, toastOptions);
        }
      })
      .catch((error) => {
        const toastOptions: ExternalToast = {
          position: 'bottom-right',
          duration: Infinity,
        };

        console.log('error', error);
        toast.error(`Failed! Status: ${error.status} - ${error.message}`, toastOptions);
      });
  };

  const reScore = async () => {
    await reScoreInterviewByIdRequest({ interviewId })
      .unwrap()
      .then((x) => {
        const toastOptions: ExternalToast = {
          position: 'bottom-right',
          duration: Infinity,
        };

        if (x && interviewId in x) {
          toast.success(`Re-score initiated - may take a few minutes. Re-score ID: ${x[interviewId]}`, toastOptions);
        } else {
          toast.error(`Re-score failed - did not receive an scoring ID.`, toastOptions);
        }
      })
      .catch((error) => {
        const toastOptions: ExternalToast = {
          position: 'bottom-right',
          duration: Infinity,
        };
        toast.error(`Re score failed! Status: ${error.status} - ${error.message}`, toastOptions);
      });
  };

  if (scoreInterviewQuery.isError) {
    return <IsAxiosError error={scoreInterviewQuery.error} />;
  }

  if (scoreInterviewQuery.isFetching || scoreInterviewQuery.isLoading || !scoreInterviewQuery.data) {
    return <FullViewLoading title="Loading scoring page" subTitle={`Scoring of interview ${interviewId}.`} />;
  }

  const contextValue = {
    user,
    template,
    scoreTable,
    setScoreTable,
    scoreTableDetails,

    job,
    candidate,
    application,
    interview,

    // Interview summaries
    summary,
    existingInterviewSummary,

    aiDetectionSummary,
    autoUpdateSummary,
    chat,

    languageList,
    setLanguageList,

    preferredLanguage,
    setPreferredLanguage,

    updateIntegration,
    setUpdateIntegration,

    chatFilter,
    setChatFilter,
    resetChatFilter,

    updateScoreData,
    saveScores,
    isManuallyScoreInterviewByIdRequestLoading,

    reScore,
    isReScoreInterviewByIdRequestLoading,
  };

  return <ScoreInterviewContext.Provider value={contextValue}>{children}</ScoreInterviewContext.Provider>;
};

export const useScoreInterview = () => {
  return useContext(ScoreInterviewContext) as ScoreInterviewContextType;
};
